// Fix GT (2026-10-17): "En pagos podemos agregar también 'perfiles de aprobación' al final, así como tenemos en
// compras" · "que los perfiles de 'Dirección general' y 'Administración' sean los que puedan editar esto" (CEN).
// Quién aprueba las Órdenes de Pago, por perfil y por monto. La regla vive en el backend
// (routers/aprobacion_doble.py → estado_op); acá se configura y se muestra.
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ShieldCheck, X } from "lucide-react";
import { API } from "../contexts/AuthContext";
import { formatPYG } from "../utils/format";
import { normalizeRole } from "../utils/roles";
import SelectBuscable from "../components/SelectBuscable";

const TIPOS_PAGO = [
  { id: "contado", nombre: "Contado" },
  { id: "credito", nombre: "A crédito" },
  { id: "tercerizados", nombre: "Tercerizados" },
  { id: "salarios", nombre: "Salarios y liquidaciones" },
];
// Fix IS (2026-11-02): "mostrar los 4 tipos juntos (contado, crédito, tercerizados y liquidaciones), en vez de
// elegir uno por vez" (reunión CEN, 30/10). Los rangos generales y los de cada tipo se ven y se editan a la vez.
const BLOQUES_PAGO = [{ id: "general", nombre: "General" }, ...TIPOS_PAGO];

const miles = (n) => (n == null || n === "" ? "" : Number(n).toLocaleString("es-PY", { maximumFractionDigits: 0 }));
const nuevoId = () => `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
const enPerfil = (perfil, u) => (perfil.user_ids || []).includes(u.id)
  || (!!perfil.rol && normalizeRole(u.role) === normalizeRole(perfil.rol));

export const textoTramoOP = (t) => (t?.hasta == null
  ? (t?.desde ? `más de ${formatPYG(t.desde)}` : "cualquier monto")
  : (t.desde ? `de ${formatPYG(t.desde)} a ${formatPYG(t.hasta)}` : `hasta ${formatPYG(t.hasta)}`));

// Quién aprobó y quién falta en una OP pendiente (lo calcula el backend en `aprobacion_perfiles`).
// `compacto` (listado): solo el perfil; quién aprobó queda en el título (al pasar el mouse) y en el detalle.
export function ChipsAprobacionOP({ ap, testId, compacto = false }) {
  if (!ap) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1 justify-center" data-testid={testId}>
      {ap.requeridos.map(p => {
        const firma = ap.vigentes?.[p.id] || ap.acreditaciones?.[p.id];
        return firma ? (
        <span key={p.id} title={`${p.nombre}: ${firma.by_name}${firma.criterio_derivado ? " · acreditado por la misma firma de " + firma.perfil : ""}`}
          className={`text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 ${compacto ? "rounded-md leading-tight" : "rounded-full whitespace-nowrap"}`}>
          ✓ {p.nombre}{compacto ? "" : `: ${firma.by_name}`}{firma.criterio_derivado ? " · misma firma" : ""}
        </span>
      ) : (
        <span key={p.id} className={`text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 ${compacto ? "rounded-md leading-tight" : "rounded-full whitespace-nowrap"}`}>
          Falta: {p.nombre}
        </span>
      ); })}
    </div>
  );
}

function Chips({ perfiles, elegidos, onToggle, disabled, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {perfiles.map(p => {
        const on = elegidos.includes(p.id);
        return (
          <button key={p.id} type="button" disabled={disabled} onClick={() => onToggle(p.id)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all disabled:cursor-default ${on ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-300 hover:border-[#2E6DA4]/50"}`}
            data-testid={`${testId}-${p.id}`}>
            {on ? "✓ " : ""}{p.nombre || "Sin nombre"}
          </button>
        );
      })}
    </div>
  );
}

export default function PerfilesAprobacionPagos() {
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/rangos-aprobacion-pagos`, { withCredentials: true })
      .then(({ data }) => setCfg(data))
      .catch(err => setError(err.response?.data?.detail || "No se pudo cargar la configuración"));
  }, []);

  if (error) return <p className="text-sm text-gray-500 py-8 text-center">{error}</p>;
  if (!cfg) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-gray-400" size={22} /></div>;

  const ro = !cfg.puede_editar;
  const usuarios = cfg.usuarios || [];
  const perfiles = cfg.perfiles || [];
  const categorias = cfg.categorias || {};
  // Fix IS: cada función recibe el bloque (General o un tipo) en vez de leer un tipo elegido.
  const tramosDe = (cat) => (cat === "general" ? (cfg.tramos || []) : (categorias[cat] || []));
  const set = (cambios) => setCfg(c => ({ ...c, ...cambios }));
  const setTramos = (cat, lista) => set(cat === "general"
    ? { tramos: lista }
    : { categorias: { ...categorias, [cat]: lista } });
  const setPerfil = (id, cambios) => set({ perfiles: perfiles.map(p => (p.id === id ? { ...p, ...cambios } : p)) });
  // Una persona elegida en un perfil sale de los demás (aprueba una sola vez).
  const agregarPersona = (id, uid) => set({
    perfiles: perfiles.map(p => (p.id === id
      ? { ...p, user_ids: [...new Set([...(p.user_ids || []), uid])] }
      : { ...p, user_ids: (p.user_ids || []).filter(x => x !== uid) })),
  });
  const quitarPerfil = (id) => set({
    perfiles: perfiles.filter(p => p.id !== id),
    tramos: (cfg.tramos || []).map(t => ({ ...t, perfiles: (t.perfiles || []).filter(x => x !== id) })),
    categorias: Object.fromEntries(Object.entries(categorias).map(([cat, lista]) => [cat,
      lista.map(t => ({ ...t, perfiles: (t.perfiles || []).filter(x => x !== id) }))])),
  });
  const toggle = (lista, id) => (lista.includes(id) ? lista.filter(x => x !== id) : [...lista, id]);
  const setTramo = (cat, i, cambios) => setTramos(cat, tramosDe(cat).map((t, j) => (j === i ? { ...t, ...cambios } : t)));
  const agregarTramo = (cat) => {
    const tramos = tramosDe(cat);
    if (!tramos.length) { setTramos(cat, [{ hasta: null, perfiles: perfiles[0] ? [perfiles[0].id] : [] }]); return; }
    const previo = tramos.length > 1 ? Number(tramos[tramos.length - 2].hasta) || 0 : 0;
    const nuevo = { hasta: previo + 10000000, perfiles: [...(tramos[tramos.length - 1].perfiles || [])] };
    setTramos(cat, [...tramos.slice(0, -1), nuevo, tramos[tramos.length - 1]]);
  };
  const quitarTramo = (cat, i) => setTramos(cat, tramosDe(cat).filter((_, j) => j !== i)
    .map((t, j, arr) => (j === arr.length - 1 ? { ...t, hasta: null } : t)));

  const guardar = async () => {
    if (perfiles.some(p => !(p.nombre || "").trim())) { toast.error("Cada perfil tiene que tener un nombre"); return; }
    const grupos = [["General", cfg.tramos || []],
      ...TIPOS_PAGO.map(t => [t.nombre, categorias[t.id] || []])];
    for (const [nombre, lista] of grupos) {
      for (let i = 0; i < lista.length - 1; i++) {
        const h = Number(lista[i].hasta) || 0;
        if (h <= (i ? Number(lista[i - 1].hasta) || 0 : 0)) {
          toast.error(nombre + ": los montos de los tramos tienen que ir de menor a mayor"); return;
        }
      }
      if (lista.some(t => !(t.perfiles || []).length)) {
        toast.error(nombre + ": cada tramo tiene que tener al menos un perfil"); return;
      }
    }
    const serializar = (lista) => lista.map((t, i) => ({
      hasta: i === lista.length - 1 ? null : Number(t.hasta) || 0, perfiles: t.perfiles || [],
    }));
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings/rangos-aprobacion-pagos`, {
        perfiles: perfiles.map(({ id, nombre, rol, user_ids }) => ({ id, nombre, rol: rol || "", user_ids: user_ids || [] })),
        tramos: serializar(cfg.tramos || []),
        categorias: Object.fromEntries(TIPOS_PAGO.filter(t => (categorias[t.id] || []).length)
          .map(t => [t.id, serializar(categorias[t.id])])),
      }, { withCredentials: true });
      setCfg(data);
      toast.success("Perfiles de aprobación de Pagos guardados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const card = "bg-white rounded-2xl border border-gray-200/60 shadow-sm p-4 sm:p-5";
  return (
    <div className="space-y-5 max-w-5xl" data-testid="perfiles-pagos-tab">
      <div className={card}>
        <h2 className="font-bold text-gray-900 flex items-center gap-2"><ShieldCheck size={18} className="text-[#2E6DA4]" /> Perfiles de aprobación — Órdenes de Pago</h2>
        <p className="text-xs text-gray-500 mt-1">Quién aprueba cada Orden de Pago según su tipo y monto. Cada tipo puede tener rangos propios; si no los tiene, usa los rangos generales. Se conservan las reglas vigentes de aprobación. Para aprobar tienen que completar su aprobación todos los perfiles exigidos por el tramo. Un perfil sin personas no se exige.</p>
        {ro && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3" data-testid="perfiles-pagos-ro">Solo lectura: lo cambian Administración y Dirección General.</p>}
      </div>

      {/* Perfiles */}
      <div className={card} data-testid="perfiles-pagos-perfiles">
        <h3 className="text-sm font-bold text-gray-900">Perfiles de aprobación</h3>
        <p className="text-xs text-gray-600 mt-2" data-testid="pp-director-obligatorio">Los pagos ajenos a compras de materiales y servicios requieren Dirección General, respetando las personas elegidas en ese perfil. Si el tramo ya exige Dirección, conserva su firma independiente; si no, una firma válida de su miembro puede acreditar también ese criterio. No cambia pagos ya aprobados.</p>
        <p className="text-[11px] text-gray-500 mb-3">Aprueban las personas elegidas y, si el perfil tiene un rol, también todos los usuarios con ese rol. Una persona va en un solo perfil.</p>
        <div className="space-y-3">
          {perfiles.map(p => {
            const aprueban = usuarios.filter(u => enPerfil(p, u));
            return (
              <div key={p.id} className="border border-gray-200 rounded-xl p-3 space-y-2" data-testid={`pp-perfil-${p.id}`}>
                <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_14rem_auto] gap-2 items-center">
                  <input value={p.nombre} disabled={ro} onChange={e => setPerfil(p.id, { nombre: e.target.value })}
                    placeholder="Nombre del perfil" className="min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold disabled:bg-gray-50"
                    data-testid={`pp-perfil-nombre-${p.id}`} />
                  <select value={p.rol || ""} disabled={ro} onChange={e => setPerfil(p.id, { rol: e.target.value })}
                    className="order-3 sm:order-none col-span-2 sm:col-span-1 min-w-0 border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white disabled:bg-gray-50" data-testid={`pp-perfil-rol-${p.id}`}>
                    <option value="">Sin rol (solo las personas elegidas)</option>
                    {(cfg.roles || []).map(r => <option key={r} value={r}>Rol: {r}</option>)}
                  </select>
                  {!ro && (
                    <button type="button" onClick={() => quitarPerfil(p.id)} title="Quitar perfil"
                      className="order-2 sm:order-none p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`pp-perfil-quitar-${p.id}`}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {(p.user_ids || []).map(uid => {
                    const u = usuarios.find(x => x.id === uid);
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 text-[11px] bg-[#EBF3FB] text-[#1A4B7A] border border-[#2E6DA4]/20 rounded-full pl-2 pr-1 py-0.5">
                        {u ? u.name : "Usuario inactivo"}
                        {!ro && <button type="button" onClick={() => setPerfil(p.id, { user_ids: p.user_ids.filter(x => x !== uid) })} className="p-0.5 min-h-0 hover:text-red-600" title="Quitar"><X size={11} /></button>}
                      </span>
                    );
                  })}
                  {!ro && (
                    <div className="w-full sm:w-64">
                      <SelectBuscable value="" onChange={uid => uid && agregarPersona(p.id, uid)}
                        options={usuarios.filter(u => !(p.user_ids || []).includes(u.id)).map(u => ({ value: u.id, label: `${u.name} · ${u.role}` }))}
                        vacio="+ Agregar persona" placeholderBuscar="Buscar persona..."
                        className="w-full border border-dashed border-gray-300 rounded-lg px-2.5 py-1.5 text-xs bg-white" testId={`pp-perfil-persona-${p.id}`} />
                    </div>
                  )}
                </div>
                <p className={`text-[11px] ${aprueban.length ? "text-gray-500" : "text-amber-700"}`} data-testid={`pp-perfil-aprueban-${p.id}`}>
                  {aprueban.length ? `Aprueban: ${aprueban.map(u => u.name).join(", ")}` : "Sin personas: este perfil no se exige."}
                </p>
              </div>
            );
          })}
        </div>
        {!ro && (
          <button type="button" onClick={() => set({ perfiles: [...perfiles, { id: nuevoId(), nombre: "", rol: "", user_ids: [] }] })}
            className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#2E6DA4] border border-dashed border-[#2E6DA4]/50 rounded-lg px-3 py-2 hover:bg-blue-50" data-testid="pp-perfil-agregar">
            <Plus size={13} /> Agregar perfil
          </button>
        )}
      </div>

      {/* Tramos — Fix IS: General y los 4 tipos, todos a la vista */}
      <div className={card} data-testid="perfiles-pagos-tramos">
        <h3 className="text-sm font-bold text-gray-900">Órdenes de Pago — por tipo y monto</h3>
        <p className="text-[11px] text-gray-500 mt-1 mb-3">Cada tramo dice qué perfiles aprueban una OP de ese monto (en una aprobación parcial cuenta la parte que se aprueba). Cada aprobación queda con su monto: si se aprueba otro monto, se aprueba de nuevo. Un tipo sin rangos propios usa los rangos generales.</p>
        <div className="space-y-4">
          {BLOQUES_PAGO.map(b => {
            const tramos = tramosDe(b.id);
            const general = b.id === "general";
            return (
              <div key={b.id} className="border border-gray-200 rounded-xl p-3 sm:p-4 bg-gray-50/40" data-testid={`pp-bloque-${b.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <p className="text-sm font-bold text-gray-800">{b.nombre}</p>
                  <span className={`text-[11px] font-semibold ${tramos.length ? "text-[#2E6DA4]" : "text-gray-500"}`} data-testid={`pp-bloque-estado-${b.id}`}>
                    {general ? "Respaldo para los tipos sin rangos propios" : tramos.length ? "Rangos propios" : "Usa los rangos generales"}
                  </span>
                </div>
                <div className="space-y-2">
                  {tramos.map((t, i) => {
                    const ultimo = i === tramos.length - 1;
                    const desde = i ? Number(tramos[i - 1].hasta) || 0 : 0;
                    return (
                      <div key={i} className="border border-gray-200 bg-white rounded-xl p-3 flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3" data-testid={`pp-${b.id}-tramo-${i}`}>
                        <div className="flex items-center gap-2 text-xs text-gray-700 flex-wrap lg:w-80 flex-shrink-0">
                          <span className="text-gray-500">{desde ? `Más de ${formatPYG(desde)}` : "Desde Gs. 0"}</span>
                          {ultimo ? (
                            <span className="font-semibold">· sin límite</span>
                          ) : (
                            <label className="flex items-center gap-1.5">
                              <span className="text-gray-500">hasta Gs.</span>
                              <input inputMode="numeric" value={miles(t.hasta)} disabled={ro}
                                onChange={e => setTramo(b.id, i, { hasta: Number(e.target.value.replace(/\D/g, "")) || 0 })}
                                className="w-32 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right font-semibold disabled:bg-gray-50"
                                data-testid={`pp-${b.id}-tramo-hasta-${i}`} />
                            </label>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <Chips perfiles={perfiles} elegidos={t.perfiles || []} disabled={ro} testId={`pp-${b.id}-tramo-${i}-perfil`}
                            onToggle={id => setTramo(b.id, i, { perfiles: toggle(t.perfiles || [], id) })} />
                        </div>
                        {!ro && (
                          <button type="button" onClick={() => quitarTramo(b.id, i)} title="Quitar tramo"
                            className="self-start lg:self-center p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`pp-${b.id}-tramo-quitar-${i}`}>
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {!tramos.length && <p className="text-xs text-gray-500 italic" data-testid={`pp-${b.id}-sin-tramos`}>
                    {general ? "Sin rangos generales: se conservan las reglas vigentes de aprobación."
                      : "Este tipo usa los rangos generales. Agregá un tramo para configurar sus aprobadores propios."}
                  </p>}
                </div>
                {!ro && (
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button type="button" onClick={() => agregarTramo(b.id)}
                      className="flex items-center gap-1.5 text-xs font-semibold text-[#2E6DA4] border border-dashed border-[#2E6DA4]/50 rounded-lg px-3 py-2 hover:bg-blue-50" data-testid={`pp-${b.id}-tramo-agregar`}>
                      <Plus size={13} /> Agregar tramo
                    </button>
                    {!general && tramos.length > 0 && <button type="button" onClick={() => setTramos(b.id, [])}
                      className="text-xs text-gray-600 underline" data-testid={`pp-${b.id}-usar-general`}>Usar rangos generales</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {!ro && (
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={guardar} disabled={saving}
            className="px-5 py-2.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white text-sm font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2" data-testid="pp-guardar">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <ShieldCheck size={15} />} Guardar
          </button>
          {cfg.updated_at && <span className="text-[11px] text-gray-400">Última actualización: {new Date(cfg.updated_at).toLocaleString("es-PY")}{cfg.updated_by ? ` · ${cfg.updated_by}` : ""}</span>}
        </div>
      )}
    </div>
  );
}
